import { Search } from "@mui/icons-material";
import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import {
  AutoCompleteSelectedValue,
  OptionItem,
} from "commons/ultilities/dynamic-form/models/control";
import { debounce } from "lodash";
import {
  HTMLAttributes,
  KeyboardEventHandler,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { XTextField } from "../text-field/x-text-field";

const EMPTY_OPTIONS: OptionItem[] = [];
const MAX_RESOLVED_SELECTED_OPTION_CACHE_SIZE = 50;

type XAutoCompleteProps = {
  onValueChanged?: (optionItem: OptionItem | null) => void;
  value?: any;
  isValid?: boolean;
  searchFunc?: (keyword: string) => Promise<OptionItem[]>;
  label?: string;
  helpText?: string;
  placeholder?: string;
  defaultOptions?: OptionItem[];
  searchOnFocus?: boolean;
  searchWhenRender?: boolean;
  open?: boolean;
  onOpenChanged?: (isOpen: boolean) => void;
  disableCloseOnSelect?: boolean;
  getOptionDisabled?: (optionItem: OptionItem) => boolean;
  isOptionInteractionDisabled?: (optionItem: OptionItem) => boolean;
  resolveSelectedOptionAsync?: (
    value: AutoCompleteSelectedValue,
  ) => Promise<OptionItem | null>;
};
export const XAutoComplete = ({
  onValueChanged,
  value,
  isValid = true,
  searchFunc,
  label = "",
  helpText = "",
  placeholder = "",
  defaultOptions = EMPTY_OPTIONS,
  searchOnFocus = true,
  searchWhenRender = false,
  open,
  onOpenChanged,
  disableCloseOnSelect = false,
  getOptionDisabled,
  isOptionInteractionDisabled,
  resolveSelectedOptionAsync,
}: XAutoCompleteProps) => {
  const [searching, setSearching] = useState<boolean>(false);
  const [optionItems, setOptionItems] = useState<OptionItem[]>(defaultOptions);
  const [resolvedSelectedOption, setResolvedSelectedOption] =
    useState<OptionItem | null>(null);
  const searchRequestIdRef = useRef<number>(0);
  const selectedResolveRequestIdRef = useRef<number>(0);
  const resolvedSelectedOptionCacheRef = useRef<
    Map<AutoCompleteSelectedValue, OptionItem | null>
  >(new Map());

  const getCachedResolvedSelectedOption = useCallback(
    (nextValue: AutoCompleteSelectedValue) => {
      const cachedOption =
        resolvedSelectedOptionCacheRef.current.get(nextValue);
      if (cachedOption === undefined) {
        return undefined;
      }

      // Refresh insertion order for basic LRU behavior.
      resolvedSelectedOptionCacheRef.current.delete(nextValue);
      resolvedSelectedOptionCacheRef.current.set(nextValue, cachedOption);
      return cachedOption;
    },
    [],
  );

  const setCachedResolvedSelectedOption = useCallback(
    (nextValue: AutoCompleteSelectedValue, nextOption: OptionItem | null) => {
      resolvedSelectedOptionCacheRef.current.delete(nextValue);
      resolvedSelectedOptionCacheRef.current.set(nextValue, nextOption);

      if (
        resolvedSelectedOptionCacheRef.current.size >
        MAX_RESOLVED_SELECTED_OPTION_CACHE_SIZE
      ) {
        const oldestKey = resolvedSelectedOptionCacheRef.current
          .keys()
          .next().value;
        if (oldestKey !== undefined) {
          resolvedSelectedOptionCacheRef.current.delete(oldestKey);
        }
      }
    },
    [],
  );

  const isResolvableSelectedValue = useCallback(
    (nextValue: unknown): nextValue is AutoCompleteSelectedValue => {
      return typeof nextValue === "string" || typeof nextValue === "number";
    },
    [],
  );

  const beginSearchRequest = useCallback(() => {
    searchRequestIdRef.current += 1;
    return searchRequestIdRef.current;
  }, []);

  const isLatestSearchRequest = useCallback((requestId: number) => {
    return searchRequestIdRef.current === requestId;
  }, []);

  useEffect(() => {
    // For controls without async search, options are provided externally via defaultOptions.
    if (!searchFunc) {
      setOptionItems(defaultOptions || []);
    }
  }, [defaultOptions, searchFunc]);

  useEffect(() => {
    let cancelled = false;

    const searchOnRenderHandler = async () => {
      if (!searchWhenRender || !searchFunc) return;
      const requestId = beginSearchRequest();

      try {
        if (!cancelled && isLatestSearchRequest(requestId)) {
          setSearching(true);
        }

        const res = await searchFunc("");
        if (!cancelled && isLatestSearchRequest(requestId)) {
          setOptionItems(res || []);
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled && isLatestSearchRequest(requestId)) {
          setSearching(false);
        }
      }
    };

    searchOnRenderHandler();

    return () => {
      cancelled = true;
    };
  }, [beginSearchRequest, isLatestSearchRequest, searchFunc, searchWhenRender]);

  useEffect(() => {
    let disposed = false;

    const resolveSelectedOption = async () => {
      if (value == null || value === "") {
        setResolvedSelectedOption(null);
        return;
      }

      const matchedOption = optionItems.find((x) => x.value === value);
      if (matchedOption) {
        setResolvedSelectedOption(null);
        return;
      }

      if (!resolveSelectedOptionAsync || !isResolvableSelectedValue(value)) {
        setResolvedSelectedOption(null);
        return;
      }

      const cachedResolvedOption = getCachedResolvedSelectedOption(value);
      if (cachedResolvedOption !== undefined) {
        setResolvedSelectedOption(cachedResolvedOption);
        return;
      }

      selectedResolveRequestIdRef.current += 1;
      const currentRequestId = selectedResolveRequestIdRef.current;

      try {
        const resolvedOption = await resolveSelectedOptionAsync(value);
        setCachedResolvedSelectedOption(value, resolvedOption || null);
        if (
          !disposed &&
          currentRequestId === selectedResolveRequestIdRef.current &&
          resolvedOption
        ) {
          setResolvedSelectedOption(resolvedOption);
          return;
        }
      } catch (error) {
        console.error(error);
      }

      if (
        !disposed &&
        currentRequestId === selectedResolveRequestIdRef.current
      ) {
        setCachedResolvedSelectedOption(value, null);
        setResolvedSelectedOption(null);
      }
    };

    resolveSelectedOption();

    return () => {
      disposed = true;
    };
  }, [
    isResolvableSelectedValue,
    getCachedResolvedSelectedOption,
    optionItems,
    resolveSelectedOptionAsync,
    setCachedResolvedSelectedOption,
    value,
  ]);

  const onValueChange = useCallback(
    (
      event: SyntheticEvent,
      value: OptionItem | null,
      reason: AutocompleteChangeReason,
    ) => {
      onValueChanged?.(value ?? null);
    },
    [onValueChanged],
  );

  const debouncedSearch = useMemo(
    () =>
      debounce(async (keyword: string) => {
        if (!searchFunc) {
          return;
        }

        const requestId = beginSearchRequest();

        try {
          if (isLatestSearchRequest(requestId)) {
            setSearching(true);
          }

          if (!keyword) {
            if (searchOnFocus || searchWhenRender) {
              const res = await searchFunc?.("");
              if (isLatestSearchRequest(requestId)) {
                setOptionItems(res || []);
              }
            } else {
              setTimeout(() => {
                if (isLatestSearchRequest(requestId)) {
                  setOptionItems([]);
                }
              }, 0);
            }
            return;
          }
          const res = await searchFunc?.(keyword);

          if (isLatestSearchRequest(requestId)) {
            setOptionItems(res || []);
          }
        } catch (e) {
          console.error(e);
        } finally {
          if (isLatestSearchRequest(requestId)) {
            setSearching(false);
          }
        }
      }, 800),
    [
      beginSearchRequest,
      isLatestSearchRequest,
      searchFunc,
      searchOnFocus,
      searchWhenRender,
    ],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const onInputChangeHandler = useCallback(
    async (
      event: SyntheticEvent,
      value: string,
      reason: AutocompleteInputChangeReason,
    ) => {
      if (reason === "reset") return;
      await debouncedSearch(value);
    },
    [debouncedSearch],
  );

  const selectedValue = useMemo(() => {
    const matchedOption = optionItems.find((x) => value === x.value);
    if (matchedOption) {
      return matchedOption;
    }

    if (resolvedSelectedOption && resolvedSelectedOption.value === value) {
      return resolvedSelectedOption;
    }

    if (value == null || value === "") {
      return null;
    }

    return {
      value,
      label: String(value),
    } as OptionItem;
  }, [optionItems, resolvedSelectedOption, value]);

  const optionsWithSelectedValue = useMemo(() => {
    const currentOptions = optionItems || [];

    if (!selectedValue) {
      return currentOptions;
    }

    const optionsWithoutSelected = currentOptions.filter(
      (x) => x?.value !== selectedValue.value,
    );

    return [selectedValue, ...optionsWithoutSelected];
  }, [optionItems, selectedValue]);

  const onKeyDownHandler: KeyboardEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (selectedValue && e.key === "Backspace") {
        onValueChanged?.(null);
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (value && e.key !== "Tab") {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [onValueChanged, selectedValue, value],
  );

  const openHandler = useCallback(async () => {
    onOpenChanged?.(true);

    if (!searchOnFocus || !searchFunc) return;
    const requestId = beginSearchRequest();

    try {
      if (isLatestSearchRequest(requestId)) {
        setSearching(true);
      }

      const res = await searchFunc("");
      if (isLatestSearchRequest(requestId)) {
        setOptionItems(res || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      if (isLatestSearchRequest(requestId)) {
        setSearching(false);
      }
    }
  }, [
    beginSearchRequest,
    isLatestSearchRequest,
    onOpenChanged,
    searchFunc,
    searchOnFocus,
  ]);

  const closeHandler = useCallback(() => {
    onOpenChanged?.(false);
  }, [onOpenChanged]);

  const renderOptionHandler = useCallback(
    (
      props: HTMLAttributes<HTMLLIElement>,
      option: OptionItem,
      state: { index: number },
    ) => {
      const isPinnedSelectedOption =
        state.index === 0 &&
        selectedValue != null &&
        optionsWithSelectedValue[0]?.value === selectedValue.value &&
        option.value === selectedValue.value;
      const shouldShowSelectedDivider =
        isPinnedSelectedOption && optionsWithSelectedValue.length > 1;
      const optionContent = option.renderOption
        ? option.renderOption
        : option.label;
      const disableInteraction = isPinnedSelectedOption
        ? true
        : isOptionInteractionDisabled?.(option) === true;
      if (!disableInteraction) {
        return (
          <li {...props} key={option.value}>
            {optionContent}
          </li>
        );
      }

      const {
        onClick: _onClick,
        onMouseMove: _onMouseMove,
        onMouseOver: _onMouseOver,
        onTouchStart: _onTouchStart,
        onMouseDown: _onMouseDown,
        onKeyDown: _onKeyDown,
        ...nonInteractiveProps
      } = props;

      return (
        <li
          {...nonInteractiveProps}
          key={option.value}
          tabIndex={-1}
          style={{
            ...(nonInteractiveProps.style || {}),
            backgroundColor: isPinnedSelectedOption
              ? "rgba(25, 118, 210, 0.08)"
              : "transparent",
            cursor: "default",
            pointerEvents: "auto",
            opacity: 1,
          }}
        >
          <Stack width={"100%"} spacing={0.5}>
            <Stack
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              spacing={1}
            >
              <Stack flex={1} minWidth={0}>
                {optionContent}
              </Stack>
              {isPinnedSelectedOption && (
                <Typography
                  variant="caption"
                  color="primary.main"
                  fontWeight={600}
                >
                  Đã chọn
                </Typography>
              )}
            </Stack>
            {shouldShowSelectedDivider && (
              <Divider sx={{ borderBottomStyle: "dashed" }} />
            )}
          </Stack>
        </li>
      );
    },
    [isOptionInteractionDisabled, optionsWithSelectedValue, selectedValue],
  );

  const getOptionDisabledHandler = useCallback(
    (option: OptionItem) => {
      const disabledByConsumer = getOptionDisabled?.(option) === true;
      const isPinnedSelectedOption =
        selectedValue != null &&
        optionsWithSelectedValue[0]?.value === selectedValue.value &&
        option.value === selectedValue.value;

      return disabledByConsumer || isPinnedSelectedOption;
    },
    [getOptionDisabled, optionsWithSelectedValue, selectedValue],
  );

  return (
    <Autocomplete
      onOpen={openHandler}
      onClose={closeHandler}
      openOnFocus={searchOnFocus}
      open={open}
      disableCloseOnSelect={disableCloseOnSelect}
      defaultValue={null}
      loading={searching}
      loadingText="Đang tải..."
      value={selectedValue || null}
      fullWidth
      options={optionsWithSelectedValue}
      getOptionDisabled={getOptionDisabledHandler}
      isOptionEqualToValue={(option, value) =>
        option && value != null ? option?.value === value?.value : false
      }
      getOptionLabel={(item: OptionItem) => item.label}
      renderOption={renderOptionHandler}
      filterOptions={searchFunc ? (x) => x : undefined}
      onChange={onValueChange}
      onInputChange={onInputChangeHandler}
      noOptionsText={
        <Stack direction={"row"} alignItems={"center"} spacing={0.5}>
          <Search></Search>
          <Typography>Nhập dữ liệu cần tìm.</Typography>
        </Stack>
      }
      renderInput={
        !isValid
          ? ({ ...params }) => (
              <XTextField
                {...params}
                variant="outlined"
                label={label}
                error={true}
                helperText={helpText || ""}
                onKeyDown={onKeyDownHandler}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: searching ? (
                    <CircularProgress size={15}></CircularProgress>
                  ) : (
                    params.InputProps.endAdornment
                  ),
                }}
                placeholder={placeholder}
              />
            )
          : (params) => (
              <XTextField
                {...params}
                variant="outlined"
                label={label}
                onKeyDown={onKeyDownHandler}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: searching ? (
                    <CircularProgress size={15}></CircularProgress>
                  ) : (
                    params.InputProps.endAdornment
                  ),
                }}
                placeholder={placeholder}
              />
            )
      }
    />
  );
};
